import jsPDF from "jspdf";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { getCompanyData, CompanyData } from "@/components/CompanySettings";

// Corporate Design Colors
const COLORS = {
  primary: [41, 128, 185] as [number, number, number],
  secondary: [52, 73, 94] as [number, number, number],
  text: [44, 62, 80] as [number, number, number],
  border: [189, 195, 199] as [number, number, number],
  background: [236, 240, 241] as [number, number, number],
  gold: [212, 175, 55] as [number, number, number],
};

/**
 * Loads image from URL and returns base64 data
 */
const loadImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("Could not load image:", e);
    return null;
  }
};

/**
 * Adds company logo to the document
 * Default size is 60x33 (50% larger than before)
 */
const addCompanyLogo = async (doc: jsPDF, logoBase64: string | null, x: number = 15, y: number = 8, width: number = 60, height: number = 33): Promise<void> => {
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'AUTO', x, y, width, height);
    } catch (e) {
      console.warn("Logo konnte nicht geladen werden:", e);
    }
  }
};

/**
 * Adds footer with company address and page numbers to all pages
 */
const addDocumentFooter = (doc: jsPDF, companyData: CompanyData): void => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageCount = doc.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Footer line
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.line(20, pageHeight - 20, pageWidth - 20, pageHeight - 20);
    
    // Company address on the left
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.secondary);
    doc.setFont("helvetica", "normal");
    
    const addressParts = [];
    if (companyData.name) addressParts.push(companyData.name);
    if (companyData.street && companyData.houseNumber) {
      addressParts.push(`${companyData.street} ${companyData.houseNumber}`);
    } else if (companyData.street) {
      addressParts.push(companyData.street);
    }
    if (companyData.postalCode && companyData.city) {
      addressParts.push(`${companyData.postalCode} ${companyData.city}`);
    }
    
    const addressLine = addressParts.join(" | ");
    doc.text(addressLine, 20, pageHeight - 12);
    
    // Contact info
    const contactParts = [];
    if (companyData.phone) contactParts.push(`Tel: ${companyData.phone}`);
    if (companyData.email) contactParts.push(`E-Mail: ${companyData.email}`);
    if (contactParts.length > 0) {
      doc.text(contactParts.join(" | "), 20, pageHeight - 8);
    }
    
    // Page number on the right
    doc.text(`Seite ${i} von ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: "right" });
  }
};

const formatDate = (dateStr: string | undefined | null) => {
  if (!dateStr) return "";
  try {
    return format(new Date(dateStr), "dd.MM.yyyy", { locale: de });
  } catch {
    return dateStr;
  }
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
};

interface EmploymentContractData {
  personName: string;
  personAddress: string;
  personBirthDate?: string;
  position: string;
  department?: string;
  documentDate?: string;
  startDate: string;
  endDate?: string;
  probationPeriodMonths: number;
  noticePeriodWeeks: number;
  baseSalary: number;
  salaryType: string;
  vacationDays: number;
  weeklyHours: number;
  additionalTerms?: string;
  companyName: string;
  companyAddress: string;
  signatureUrl?: string;
}

export const exportEmploymentContractToPDF = async (data: EmploymentContractData, saveToFile: boolean = true): Promise<jsPDF> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let y = 20;

  // Load company logo
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  // Add company logo at top (60x33 = 50% larger)
  if (logoBase64) {
    await addCompanyLogo(doc, logoBase64, 15, 8, 60, 33);
    y = 48; // Start content below larger logo
  }

  // Location and date on the right (city only, no postal code)
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  const cityName = companyData.city || "Erftstadt";
  const documentDate = data.documentDate 
    ? format(new Date(data.documentDate), "dd.MM.yyyy", { locale: de })
    : format(new Date(), "dd.MM.yyyy", { locale: de });
  doc.text(`${cityName}, ${documentDate}`, pageWidth - margin, y, { align: "right" });
  y += 15;

  // Footer line is at pageHeight - 20 = 277. Reserve 15px buffer so text never overlaps footer.
  const MAX_CONTENT_Y = 262;
  const addNewPageIfNeeded = (requiredSpace: number = 15) => {
    if (y > MAX_CONTENT_Y - requiredSpace) {
      doc.addPage();
      y = 25;
    }
  };

  const addParagraph = (text: string, indent: number = 0) => {
    const splitLines = doc.splitTextToSize(text, pageWidth - 2 * margin - indent);
    for (const line of splitLines) {
      addNewPageIfNeeded(5);
      doc.text(line, margin + indent, y);
      y += 5;
    }
  };

  // Title
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "bold");
  doc.text("Unbefristeter Arbeitsvertrag eines gewerblichen Arbeitnehmers", pageWidth / 2, y, { align: "center" });
  y += 6;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("-ohne Tarifbindung-", pageWidth / 2, y, { align: "center" });
  y += 15;

  // Parties
  doc.setFontSize(11);
  doc.text("Zwischen der Firma", margin, y);
  y += 10;

  doc.setFont("helvetica", "bold");
  doc.text(data.companyName, margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  const companyAddressLines = data.companyAddress.split(",").map(s => s.trim());
  for (const line of companyAddressLines) {
    doc.text(line, margin, y);
    y += 5;
  }
  y += 10;

  doc.text("und Herrn/Frau", margin, y);
  y += 10;

  // Employee address in 3 lines: Name, Street + House Number, City - all normal font
  doc.setFont("helvetica", "normal");
  doc.text(data.personName, margin, y);
  y += 5;
  if (data.personAddress) {
    // Split address and format as separate lines
    const addressParts = data.personAddress.split(",").map(s => s.trim());
    for (const part of addressParts) {
      doc.text(part, margin, y);
      y += 5;
    }
  }
  if (data.personBirthDate) {
    doc.text(`geb. am ${formatDate(data.personBirthDate)}`, margin, y);
    y += 5;
  }
  y += 10;

  doc.setFont("helvetica", "bold");
  doc.text("wird folgender Arbeitsvertrag geschlossen:", margin, y);
  y += 15;

  // § 1 Beginn des Arbeitsverhältnisses und Tätigkeit
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("1. Beginn des Arbeitsverhältnisses und Tätigkeit", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph(`1.1 Der Arbeitnehmer wird ab ${formatDate(data.startDate)} als ${data.position}${data.department ? ` in der Abteilung ${data.department}` : ""} eingestellt. Vor Arbeitsantritt ist die ordentliche Kündigung ausgeschlossen.`);
  y += 2;
  addParagraph("Die Firma geht aufgrund Erklärung des Arbeitnehmers davon aus, dass dieser die persönlichen Voraussetzungen für die Ausübung seiner Tätigkeit besitzt und ohne gesundheitliche Einschränkung einsetzbar ist.");
  y += 4;

  addParagraph("1.2 Der Arbeitnehmer verpflichtet sich, bei Bedarf auch andere ihm zumutbaren Arbeiten im Betrieb zu übernehmen, wenn dadurch keine Lohnänderung eintritt. Das gilt auch, wenn er im Falle einer Erkrankung in der Lage ist, ohne Beeinträchtigung seiner Gesundheit andere ihm zumutbare Arbeiten auszuführen.");
  y += 4;

  addParagraph(`1.3 Die ersten ${data.probationPeriodMonths} Monate des Arbeitsverhältnisses gelten als Probezeit. Innerhalb der Probezeit beträgt die Kündigungsfrist 2 Wochen (§ 622 Abs. 3 BGB).`);
  y += 10;

  // § 2 Vergütung / Urlaub
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("2. Vergütung / Urlaub", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("2.1 Der Arbeitnehmer erhält eine Vergütung, die sich wie folgt zusammensetzt:");
  y += 4;

  if (data.salaryType === "monthly") {
    addParagraph(`Vereinbartes Gehalt: ${formatCurrency(data.baseSalary)} brutto pro Monat für ${data.weeklyHours} Std. pro Woche`, 10);
  } else {
    addParagraph(`Vereinbarter Stundenlohn: ${formatCurrency(data.baseSalary)} brutto pro Std. für ${data.weeklyHours} Std. pro Woche`, 10);
  }
  y += 4;

  addParagraph("Einmalzahlungen werden freiwillig gewährt. Sie kann jederzeit aus wirtschaftlichen Gründen oder aus Gründen, die im Verhalten des Arbeitnehmers liegen, widerrufen werden. Der Grund ist beim Widerruf zu nennen.");
  y += 4;

  addParagraph("2.2 Lohnabtretungen und Lohnpfändungen sind der Firma unverzüglich anzuzeigen. Zur Deckung der hierdurch entstehenden Kosten kann die Firma für die Bearbeitung 3 v.H. des jeweils an den Gläubiger überwiesenen Betrages, mindestens aber 10,00 EUR für jeden Vorgang bei der Lohnabrechnung einbehalten.");
  y += 4;

  addParagraph(`2.3 Der Arbeitnehmer erhält einen Jahresurlaub von ${data.vacationDays} Arbeitstagen. Der Jahresurlaub muss im Veranlagungsjahr (Kalenderjahr) genommen werden und verfällt, sofern er nicht rechtzeitig beantragt und genehmigt wurde, zum Jahresende. Eine Übertragung auf das Folgejahr ist nur in begründeten Ausnahmefällen und mit vorheriger schriftlicher Zustimmung des Arbeitgebers möglich.`);
  y += 4;

  addParagraph("2.4 Überstunden werden auf einem Arbeitszeitkonto erfasst. Bei Erreichen von 180 Überstunden erfolgt eine Auszahlung der darüber hinausgehenden Stunden zum regulären Stundensatz zuzüglich eines Zuschlags gemäß gesetzlicher oder tariflicher Regelungen.");
  y += 10;

  // § 3 Arbeitsvertragliche Pflichten
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("3. Arbeitsvertragliche Pflichten", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("3.1 Der Arbeitnehmer verpflichtet sich, die ihm übertragenen Arbeiten sorgfältig und gewissenhaft auszuführen.");
  y += 4;

  addParagraph("3.2 Während der Dauer des Arbeitsverhältnisses darf der Arbeitnehmer ohne vorherige Zustimmung des Arbeitgebers keine anderweitige Erwerbstätigkeit, auch keine Nebentätigkeit, ausüben.");
  y += 4;

  addParagraph("3.3 Kurzarbeit mit entsprechender Minderung des Arbeitsverdienstes kann nach einer Ankündigungsfrist von zwei Wochen zum Wochenschluss eingeführt werden. Gleichzeitig muss die voraussichtliche Dauer der Kurzarbeit bekannt gegeben werden.");
  y += 4;

  addParagraph("3.4 Im Rahmen der gesetzlichen Bestimmungen hat jeder Arbeitnehmer bei dringenden betrieblichen Erfordernissen Mehrarbeit, Nachtarbeit sowie Sonn- und Feiertagsarbeit zu leisten, wenn die Betriebsleitung dies verlangt.");
  y += 10;

  // § 4 Arbeitsfähigkeit
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("4. Arbeitsfähigkeit", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("4.1 Der Arbeitnehmer ist verpflichtet, dem Arbeitgeber bei Krankheit oder einer sonstigen Verhinderung den Grund und die voraussichtliche Dauer der Verhinderung unverzüglich mitzuteilen. Unverzüglich bedeutet vor Arbeitsbeginn telefonisch beim Geschäftsführer. Die ärztliche Bescheinigung über das Bestehen der Arbeitsunfähigkeit sowie deren voraussichtliche Dauer ist spätestens am zweiten Kalendertag der Arbeitsunfähigkeit vorzulegen. Dauert die Arbeitsunfähigkeit länger als in dieser Bescheinigung angegeben, so ist der Arbeitnehmer verpflichtet, den Arbeitgeber wiederum unverzüglich hiervon zu unterrichten und ebenfalls unverzüglich eine neue ärztliche Bescheinigung vorzulegen.");
  y += 4;

  addParagraph("4.2 Der Arbeitgeber ist berechtigt, die Vorlage der ärztlichen Bescheinigung gemäß Ziffer 4.1 auch früher zu verlangen.");
  y += 10;

  // § 5 Eingebrachte Sachen
  addNewPageIfNeeded(30);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("5. Eingebrachte Sachen", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Für Schäden an eingebrachten Sachen des Arbeitnehmers oder für Verlust derselben haftet der Arbeitgeber nur bei Vorsatz oder grober Fahrlässigkeit im Rahmen seiner Fürsorgepflicht.");
  y += 10;

  // § 6 Beendigung des Arbeitsverhältnisses
  addNewPageIfNeeded(50);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("6. Beendigung des Arbeitsverhältnisses", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph(`6.1 Wird das Arbeitsverhältnis nach der Probezeit beendet, kann es mit der gesetzlichen Kündigungsfrist von ${data.noticePeriodWeeks} Wochen zum Monatsende gekündigt werden (§ 622 BGB). Die gesetzlichen Kündigungsfristen verlängern sich bei längerer Betriebszugehörigkeit entsprechend.`);
  y += 4;

  addParagraph("6.2 Das Arbeitsverhältnis endet, ohne dass es einer Kündigung bedarf, mit Erreichen der für den Arbeitnehmer maßgeblichen gesetzlichen Regelaltersgrenze, spätestens mit Ablauf des Monats, in dem der Arbeitnehmer das 67. Lebensjahr vollendet hat.");
  y += 4;

  addParagraph("6.3 Im Falle einer Kündigung dieses Vertrages, gleichwohl durch wen sie erfolgt ist, oder während der Verhandlungen über die einvernehmliche Aufhebung des Arbeitsverhältnisses bis zu dessen Beendigung ist der Arbeitgeber berechtigt, den Arbeitnehmer bei Vorliegen eines sachlichen Grundes mit anderen Arbeiten zu beschäftigen, oder, wenn zumutbare Arbeiten nicht vorliegen, ihn unter Fortzahlung seiner Bezüge unwiderruflich oder widerruflich von der Arbeitsleistung freizustellen.");
  y += 4;

  addParagraph("Ein sachlicher Grund für die Freistellung liegt z.B. vor, wenn sie im betrieblichen Geheimhaltungsinteresse, insbesondere bei grobem Vertragsverstoß, der die Vertrauensgrundlage beeinträchtigt (Geheimverrat, Konkurrenztätigkeit etc.), notwendig ist.");
  y += 4;

  addParagraph("Andere Erwerbstätigkeit des Arbeitnehmers darf auch während der Freistellung nur nach vorheriger Anzeige und ggf. Genehmigung des Arbeitgebers ausgeübt werden. Der Arbeitnehmer ist verpflichtet, sich während der Freistellung erzielten anderweitigen Erwerb auf das vom Arbeitgeber geschuldete Entgelt anrechnen zu lassen. Der Arbeitnehmer ist verpflichtet, auch während der Freistellung eine eventuell eintretende Arbeitsunfähigkeit und deren Dauer unverzüglich anzuzeigen und nachzuweisen.");
  y += 10;

  // § 7 Ausschlussfristen / Schlussbestimmungen
  addNewPageIfNeeded(80);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("7. Ausschlussfristen / Schlussbestimmungen", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("7.1 Alle Ansprüche aus dem Arbeitsverhältnis verfallen, wenn sie nicht innerhalb einer Ausschlussfrist von drei Monaten nach Fälligkeit gegenüber dem Vertragspartner schriftlich geltend gemacht und im Falle der Ablehnung durch den Vertragspartner innerhalb von weiteren drei Monaten eingeklagt werden. Hiervon unberührt bleiben Ansprüche, die auf Handlungen wegen Vorsatz oder grober Fahrlässigkeit beruhen.");
  y += 4;

  addParagraph("Die Ausschlussfrist gilt nicht für den Anspruch eines Arbeitnehmers auf den gesetzlichen Mindestlohn. Über den Mindestlohn hinausgehende Ansprüche unterliegen der vereinbarten Ausschlussfrist.");
  y += 4;

  addParagraph("7.2 Aus reinem einseitigen Verhalten des Arbeitgebers erwachsen dem Arbeitnehmer keine vertraglichen Rechtsansprüche, sofern nicht eine mündliche oder schriftliche einvernehmliche Vertragsänderung vorliegt. Die betriebliche Übung wird hiermit ausgeschlossen.");
  y += 4;

  addParagraph("7.3 Der Arbeitnehmer ist verpflichtet, dem Arbeitgeber unverzüglich jede Änderung seiner persönlichen Verhältnisse, des Familienstandes, der Anzahl der Kinder, den Wechsel der Krankenkasse und seiner Adresse mitzuteilen.");
  y += 4;

  addParagraph("7.4 Der Arbeitnehmer verfügt über eine gültige Fahrerlaubnis. Der Arbeitgeber ist berechtigt, 1x jährlich die Fahrerlaubnis zu kontrollieren und eine Kopie zur Personalakte zu nehmen. Dies gilt nur, wenn der Arbeitnehmer betriebseigene oder fremde Fahrzeuge im Rahmen seiner Arbeitstätigkeit bewegt. Der Arbeitnehmer hat den Verlust der Fahrerlaubnis unter Angabe der voraussichtlichen Dauer unmittelbar seinem Arbeitgeber mitzuteilen.");
  y += 4;

  addParagraph("7.5 Arbeitnehmer, die an auswärtigen Arbeitsplätzen/Baustellen ihrer Tätigkeit nachgehen, sind verpflichtet, jederzeit einen Personalausweis, Reisepass oder ein Passersatzdokument mit sich zu führen und dieses auf behördliche Nachfrage vorzuzeigen.");
  y += 4;

  addParagraph("7.6 Vertragliche Nebenabreden bestehen nicht. Änderungen und Ergänzungen des vorliegenden Arbeitsvertrages haben nur Gültigkeit, wenn sie schriftlich vereinbart werden. Eine mündliche oder konkludente Änderung/Aufhebung der Schriftformklausel ist nichtig.");
  y += 4;

  addParagraph("7.7 Sollten einzelne Bestimmungen dieses Vertrages unwirksam sein oder werden, wird hierdurch die Wirksamkeit des Vertrages im Übrigen nicht berührt.");
  y += 10;

  // Additional terms
  if (data.additionalTerms) {
    addNewPageIfNeeded(40);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("8. Zusätzliche Vereinbarungen", margin, y);
    y += 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    addParagraph(data.additionalTerms);
    y += 10;
  }

  // Signature section - ensure enough space before footer (footer starts at pageHeight - 20)
  // Need at least 70px for signatures + 25px margin from footer = 95px
  const signatureSpaceNeeded = 95;
  if (y > 297 - 20 - signatureSpaceNeeded) {
    doc.addPage();
    y = 25;
  }
  y += 10;

  // Location and date line
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  const signatureCityName = companyData.city || "Erftstadt";
  doc.text(`${signatureCityName}, __________________`, margin, y);
  y += 25;

  doc.setDrawColor(...COLORS.border);
  doc.line(margin, y, pageWidth / 2 - 15, y);
  doc.line(pageWidth / 2 + 15, y, pageWidth - margin, y);
  y += 5;
  doc.setFontSize(9);
  doc.text("(Arbeitnehmer)", margin, y);
  doc.text("(Arbeitgeber)", pageWidth / 2 + 15, y);

  // Add signature image if available
  if (data.signatureUrl) {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = data.signatureUrl;
      await new Promise((resolve) => { img.onload = resolve; });
      doc.addImage(img, "PNG", margin, y - 25, 50, 20);
    } catch (e) {
      console.error("Could not add signature image:", e);
    }
  }

  // Add footer with company address and page numbers
  addDocumentFooter(doc, companyData);

  if (saveToFile) {
    doc.save(`Arbeitsvertrag_${data.personName.replace(/\s+/g, "_")}.pdf`);
  }
  return doc;
};

interface ContractAmendmentData {
  personName: string;
  personAddress: string;
  oldPosition: string;
  newPosition: string;
  oldSalary: number;
  newSalary: number;
  salaryType: string;
  documentDate?: string;
  effectiveDate: string;
  amendmentReason: string;
  additionalChanges?: string;
  companyName: string;
  companyAddress: string;
  signatureUrl?: string;
}

export const exportContractAmendmentToPDF = async (data: ContractAmendmentData, saveToFile: boolean = true): Promise<jsPDF> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let y = 25;

  // Load company logo
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  // Add company logo at top (60x33 = 50% larger)
  if (logoBase64) {
    await addCompanyLogo(doc, logoBase64, 15, 8, 60, 33);
    y = 48;
  }

  // Location and date on the right (city only, no postal code)
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  const cityName = companyData.city || "Erftstadt";
  const documentDate = data.documentDate 
    ? format(new Date(data.documentDate), "dd.MM.yyyy", { locale: de })
    : format(new Date(), "dd.MM.yyyy", { locale: de });
  doc.text(`${cityName}, ${documentDate}`, pageWidth - margin, y, { align: "right" });
  y += 15;

  // Header (only show text if no logo)
  if (!logoBase64) {
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.secondary);
    doc.text(data.companyName, margin, y);
    doc.text(data.companyAddress, margin, y + 5);
    y += 20;
  }

  // Title
  doc.setFontSize(18);
  doc.setTextColor(...COLORS.primary);
  doc.setFont("helvetica", "bold");
  doc.text("ÄNDERUNGSVEREINBARUNG", pageWidth / 2, y, { align: "center" });
  y += 10;
  doc.setFontSize(12);
  doc.text("zum Arbeitsvertrag", pageWidth / 2, y, { align: "center" });
  y += 20;

  // Parties
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "bold");
  doc.text("Zwischen", margin, y);
  y += 7;
  doc.setFont("helvetica", "normal");
  doc.text(`${data.companyName} (Arbeitgeber)`, margin + 10, y);
  y += 15;

  doc.setFont("helvetica", "bold");
  doc.text("und", margin, y);
  y += 7;
  doc.setFont("helvetica", "normal");
  doc.text(`${data.personName} (Arbeitnehmer)`, margin + 10, y);
  y += 20;

  doc.text("wird folgende Änderungsvereinbarung zum bestehenden Arbeitsvertrag geschlossen:", margin, y);
  y += 20;

  // Changes
  doc.setFont("helvetica", "bold");
  doc.text("§ 1 Änderungen", margin, y);
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.text(`Mit Wirkung zum ${formatDate(data.effectiveDate)} werden folgende Änderungen vereinbart:`, margin, y);
  y += 15;

  // Position change
  if (data.oldPosition !== data.newPosition) {
    doc.setFont("helvetica", "bold");
    doc.text("Positionsänderung:", margin, y);
    y += 7;
    doc.setFont("helvetica", "normal");
    doc.text(`Bisherige Position: ${data.oldPosition}`, margin + 10, y);
    y += 5;
    doc.text(`Neue Position: ${data.newPosition}`, margin + 10, y);
    y += 15;
  }

  // Salary change
  if (data.oldSalary !== data.newSalary) {
    doc.setFont("helvetica", "bold");
    doc.text("Vergütungsänderung:", margin, y);
    y += 7;
    doc.setFont("helvetica", "normal");
    doc.text(`Bisherige Vergütung: ${formatCurrency(data.oldSalary)} ${data.salaryType === "monthly" ? "brutto/Monat" : "brutto/Stunde"}`, margin + 10, y);
    y += 5;
    doc.text(`Neue Vergütung: ${formatCurrency(data.newSalary)} ${data.salaryType === "monthly" ? "brutto/Monat" : "brutto/Stunde"}`, margin + 10, y);
    y += 15;
  }

  // Reason
  if (data.amendmentReason) {
    doc.setFont("helvetica", "bold");
    doc.text("Grund der Änderung:", margin, y);
    y += 7;
    doc.setFont("helvetica", "normal");
    doc.text(data.amendmentReason, margin + 10, y);
    y += 15;
  }

  // Additional changes
  if (data.additionalChanges) {
    doc.setFont("helvetica", "bold");
    doc.text("Weitere Änderungen:", margin, y);
    y += 7;
    doc.setFont("helvetica", "normal");
    const splitLines = doc.splitTextToSize(data.additionalChanges, pageWidth - 2 * margin - 10);
    for (const line of splitLines) {
      if (y > 262) {
        doc.addPage();
        y = 25;
      }
      doc.text(line, margin + 10, y);
      y += 5;
    }
    y += 10;
  }

  // Standard clause
  y += 10;
  doc.setFont("helvetica", "bold");
  doc.text("§ 2 Fortgeltung", margin, y);
  y += 10;
  doc.setFont("helvetica", "normal");
  doc.text("Im Übrigen bleibt der bestehende Arbeitsvertrag unverändert.", margin, y);
  y += 30;

  // Signature section - ensure enough space before footer
  const pageHeight = doc.internal.pageSize.getHeight();
  const signatureSpaceNeeded = 80;
  if (y > pageHeight - 20 - signatureSpaceNeeded) {
    doc.addPage();
    y = 25;
  }

  // Location and date
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  const signatureCityName = companyData.city || "Erftstadt";
  doc.text(`${signatureCityName}, __________________`, margin, y);
  doc.text(`${signatureCityName}, __________________`, pageWidth / 2 + 10, y);
  y += 20;

  doc.setDrawColor(...COLORS.border);
  doc.line(margin, y, pageWidth / 2 - 10, y);
  doc.line(pageWidth / 2 + 10, y, pageWidth - margin, y);
  y += 5;
  doc.setFontSize(9);
  doc.text("Unterschrift Arbeitgeber", margin, y);
  doc.text("Unterschrift Arbeitnehmer", pageWidth / 2 + 10, y);

  if (data.signatureUrl) {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = data.signatureUrl;
      await new Promise((resolve) => { img.onload = resolve; });
      doc.addImage(img, "PNG", pageWidth / 2 + 10, y - 25, 50, 20);
    } catch (e) {
      console.error("Could not add signature image:", e);
    }
  }

  // Add footer with company address and page numbers
  addDocumentFooter(doc, companyData);

  if (saveToFile) {
    doc.save(`Vertragsaenderung_${data.personName.replace(/\s+/g, "_")}.pdf`);
  }
  return doc;
};

interface CertificateData {
  personName: string;
  certificateType: string;
  certificateTitle: string;
  certificateContent: string;
  documentDate?: string;
  issueDate: string;
  companyName: string;
  companyAddress: string;
}

export const exportCertificateToPDF = async (data: CertificateData, saveToFile: boolean = true): Promise<jsPDF> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;

  // Load company logo
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;

  // Border
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(2);
  doc.rect(10, 10, pageWidth - 20, pageHeight - 20);

  // Inner border
  doc.setLineWidth(0.5);
  doc.rect(15, 15, pageWidth - 30, pageHeight - 30);

  let y = 35;

  // Add company logo centered at top (60x33 = 50% larger)
  if (logoBase64) {
    await addCompanyLogo(doc, logoBase64, pageWidth / 2 - 30, 20, 60, 33);
    y = 60;
  }

  // Company header
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.secondary);
  doc.text(data.companyName, pageWidth / 2, y, { align: "center" });
  y += 30;

  // Title
  doc.setFontSize(24);
  doc.setTextColor(...COLORS.primary);
  doc.setFont("helvetica", "bold");
  doc.text(data.certificateTitle, pageWidth / 2, y, { align: "center" });
  y += 30;

  // Person name
  doc.setFontSize(18);
  doc.setTextColor(...COLORS.text);
  doc.text(data.personName, pageWidth / 2, y, { align: "center" });
  y += 30;

  // Content
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  const splitContent = doc.splitTextToSize(data.certificateContent, pageWidth - 60);
  for (const line of splitContent) {
    if (y > pageHeight - 80) {
      doc.addPage();
      // Re-draw borders on new page
      doc.setDrawColor(...COLORS.primary);
      doc.setLineWidth(2);
      doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
      doc.setLineWidth(0.5);
      doc.rect(15, 15, pageWidth - 30, pageHeight - 30);
      y = 35;
    }
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 7;
  }

  // Date and signature
  y = pageHeight - 60;
  doc.setFontSize(11);
  doc.text(`${data.companyAddress}`, pageWidth / 2, y, { align: "center" });
  y += 10;
  doc.text(`Ausgestellt am ${formatDate(data.issueDate)}`, pageWidth / 2, y, { align: "center" });

  // Add footer with company address and page numbers
  addDocumentFooter(doc, companyData);

  if (saveToFile) {
    doc.save(`Zertifikat_${data.personName.replace(/\s+/g, "_")}.pdf`);
  }
  return doc;
};

interface AnniversaryData {
  personName: string;
  position: string;
  yearsOfService: number;
  documentDate?: string;
  anniversaryDate: string;
  customText: string;
  companyName: string;
  companyAddress: string;
  documentTitle?: string;
  documentSubtitle?: string;
}

export const exportAnniversaryCertificateToPDF = async (data: AnniversaryData, saveToFile: boolean = true): Promise<jsPDF> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Corporate colors (from design system - cyan/blue gradient)
  const gradientStartColor: [number, number, number] = [10, 35, 80]; // Deep navy blue
  const gradientEndColor: [number, number, number] = [255, 255, 255]; // White
  const accentColor: [number, number, number] = [0, 150, 200]; // Cyan accent

  // Load company logo
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;

  // Draw gradient stripe on left side (like reference design)
  const gradientWidth = 25;
  const stripeCount = 100;
  for (let i = 0; i < stripeCount; i++) {
    const ratio = i / stripeCount;
    // Gradient from deep blue to white (left to right within the stripe)
    const r = Math.round(gradientStartColor[0] + (gradientEndColor[0] - gradientStartColor[0]) * ratio);
    const g = Math.round(gradientStartColor[1] + (gradientEndColor[1] - gradientStartColor[1]) * ratio);
    const b = Math.round(gradientStartColor[2] + (gradientEndColor[2] - gradientStartColor[2]) * ratio);
    doc.setFillColor(r, g, b);
    const x = (gradientWidth / stripeCount) * i;
    doc.rect(x, 0, gradientWidth / stripeCount + 0.5, pageHeight, 'F');
  }

  // Add watermark logo in center background (8% opacity) - centered on page
  if (logoBase64) {
    try {
      const watermarkWidth = 140;
      const watermarkHeight = 77; // Maintain aspect ratio (60:33)
      const watermarkX = (pageWidth - watermarkWidth) / 2 + 10; // Offset for gradient
      const watermarkY = (pageHeight - watermarkHeight) / 2; // Vertically centered
      
      doc.saveGraphicsState();
      // @ts-ignore - jsPDF GState for opacity
      const gState = new (doc as any).GState({ opacity: 0.08 });
      // @ts-ignore
      doc.setGState(gState);
      doc.addImage(logoBase64, 'AUTO', watermarkX, watermarkY, watermarkWidth, watermarkHeight);
      doc.restoreGraphicsState();
    } catch (e) {
      console.warn("Wasserzeichen konnte nicht hinzugefügt werden:", e);
    }
  }

  // Content area starts after gradient
  const contentStartX = gradientWidth + 15;
  const contentWidth = pageWidth - contentStartX - 20;
  let y = 35;

  // Add company logo at top (50% larger: 90x50)
  if (logoBase64) {
    try {
      await addCompanyLogo(doc, logoBase64, contentStartX, 12, 90, 50);
    } catch (e) {
      console.warn("Logo konnte nicht hinzugefügt werden:", e);
    }
    y = 70;
  }

  // Document title (configurable: Urkunde, Jubiläumsurkunde, Bescheinigung, etc.)
  const title = data.documentTitle || "Urkunde";
  doc.setFontSize(42);
  doc.setTextColor(...accentColor);
  doc.setFont("times", "italic");
  doc.text(title, contentStartX, y);
  y += 12;

  // Optional subtitle (e.g. "Teilnahme einer Schulung" for Bescheinigung)
  if (data.documentSubtitle && data.documentSubtitle.trim()) {
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.text);
    doc.setFont("helvetica", "italic");
    doc.text(data.documentSubtitle, contentStartX, y);
    y += 20;
  } else {
    y += 23;
  }

  // Employee name (no salutation - just the name)
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "bold");
  doc.text(data.personName, contentStartX, y);
  y += 25;

  // Custom text (main content)
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  const lines = data.customText.split("\n");
  for (const line of lines) {
    if (line.trim() === "") {
      y += 6;
      continue;
    }
    const splitLines = doc.splitTextToSize(line.trim(), contentWidth);
    for (const splitLine of splitLines) {
      doc.text(splitLine, contentStartX, y);
      y += 7;
    }
  }

  // Location and date
  y += 15;
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  const displayDate = data.documentDate ? formatDate(data.documentDate) : formatDate(data.anniversaryDate);
  const city = data.companyAddress.split(",").pop()?.trim() || ""; // Extract city from address
  doc.text(`${city}, den ${displayDate}`, contentStartX, y);
  y += 30;

  // Signature area
  doc.setFont("times", "italic");
  doc.setFontSize(14);
  doc.text("_________________________________", contentStartX, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Geschäftsführung", contentStartX, y);
  y += 5;
  doc.text(data.companyName, contentStartX, y);

  // NO footer - single-page ceremonial document

  if (saveToFile) {
    const safeTitle = (data.documentTitle || "Urkunde").replace(/\s+/g, "_");
    doc.save(`${safeTitle}_${data.personName.replace(/\s+/g, "_")}_${data.yearsOfService}Jahre.pdf`);
  }
  return doc;
};

interface WarningData {
  personName: string;
  personAddress: string;
  position: string;
  department?: string;
  documentDate?: string;
  warningDate: string;
  warningNumber: number;
  warningReason: string;
  warningDetails: string;
  warningConsequences: string;
  companyName: string;
  companyAddress: string;
  signatureUrl?: string;
}

export const exportWarningToPDF = async (data: WarningData, saveToFile: boolean = true): Promise<jsPDF> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let y = 25;

  // Load company logo
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  // Add company logo at top (60x33 = 50% larger)
  if (logoBase64) {
    await addCompanyLogo(doc, logoBase64, 15, 8, 60, 33);
    y = 48;
  }

  // Header (only show text if no logo)
  if (!logoBase64) {
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.secondary);
    doc.text(data.companyName, margin, y);
    doc.text(data.companyAddress, margin, y + 5);
    y += 25;
  }

  // Location and date on the right (city only, no postal code)
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  const cityName = companyData.city || "Erftstadt";
  const documentDate = data.documentDate 
    ? format(new Date(data.documentDate), "dd.MM.yyyy", { locale: de })
    : formatDate(data.warningDate);
  doc.text(`${cityName}, ${documentDate}`, pageWidth - margin, y, { align: "right" });
  y += 15;

  // Employee address in 3 lines: Name, Street, City - all normal font
  doc.setFont("helvetica", "normal");
  doc.text(data.personName, margin, y);
  y += 5;
  if (data.personAddress) {
    const addressParts = data.personAddress.split(",").map(s => s.trim());
    for (const part of addressParts) {
      doc.text(part, margin, y);
      y += 5;
    }
  }
  y += 20;

  // Title
  doc.setFontSize(14);
  doc.setTextColor(180, 0, 0);
  doc.setFont("helvetica", "bold");
  const warningTitle = data.warningNumber === 1 ? "ABMAHNUNG" : `${data.warningNumber}. ABMAHNUNG`;
  doc.text(warningTitle, margin, y);
  y += 15;

  // Content
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");

  // Helper for warning content - keep text above footer (line at 277)
  const warnMaxY = 262;
  const ensureWarnSpace = (needed: number = 5) => {
    if (y > warnMaxY - needed) {
      doc.addPage();
      y = 25;
    }
  };
  const drawWarnLine = (text: string) => {
    ensureWarnSpace();
    doc.text(text, margin, y);
    y += 5;
  };

  ensureWarnSpace(12);
  doc.text("Sehr geehrte/r Frau/Herr " + data.personName.split(" ").pop() + ",", margin, y);
  y += 12;

  ensureWarnSpace(12);
  doc.text("hiermit mahnen wir Sie wegen des nachfolgend geschilderten Verhaltens ab:", margin, y);
  y += 12;

  // Reason
  ensureWarnSpace(14);
  doc.setFont("helvetica", "bold");
  doc.text("Abmahnungsgrund:", margin, y);
  y += 7;
  doc.setFont("helvetica", "normal");
  const reasonLines = doc.splitTextToSize(data.warningReason, pageWidth - 2 * margin);
  for (const line of reasonLines) {
    ensureWarnSpace();
    doc.text(line, margin, y);
    y += 5;
  }
  y += 7;

  // Details
  ensureWarnSpace(14);
  doc.setFont("helvetica", "bold");
  doc.text("Sachverhalt:", margin, y);
  y += 7;
  doc.setFont("helvetica", "normal");
  const detailLines = doc.splitTextToSize(data.warningDetails, pageWidth - 2 * margin);
  for (const line of detailLines) {
    ensureWarnSpace();
    doc.text(line, margin, y);
    y += 5;
  }
  y += 10;

  // Legal notice
  ensureWarnSpace(7);
  doc.text("Dieses Verhalten stellt eine Verletzung Ihrer arbeitsvertraglichen Pflichten dar.", margin, y);
  y += 7;
  ensureWarnSpace(12);
  doc.text("Wir fordern Sie hiermit auf, dieses Fehlverhalten zukünftig zu unterlassen.", margin, y);
  y += 12;

  // Consequences
  ensureWarnSpace(14);
  doc.setFont("helvetica", "bold");
  doc.text("Konsequenzen:", margin, y);
  y += 7;
  doc.setFont("helvetica", "normal");
  const consLines = doc.splitTextToSize(data.warningConsequences, pageWidth - 2 * margin);
  for (const line of consLines) {
    ensureWarnSpace();
    doc.text(line, margin, y);
    y += 5;
  }
  y += 15;

  // Closing
  ensureWarnSpace(20);
  doc.text("Mit freundlichen Grüßen", margin, y);
  y += 20;

  doc.line(margin, y, margin + 60, y);
  y += 5;
  doc.setFontSize(10);
  doc.text("Geschäftsführung", margin, y);
  y += 25;

  // Acknowledgment - ensure enough space before footer
  const pageHeight = doc.internal.pageSize.getHeight();
  const ackSpaceNeeded = 60;
  if (y > pageHeight - 20 - ackSpaceNeeded) {
    doc.addPage();
    y = 25;
  }

  doc.setDrawColor(...COLORS.border);
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;
  doc.setFontSize(9);
  doc.text("Empfangsbestätigung:", margin, y);
  y += 7;
  doc.text("Mit meiner Unterschrift bestätige ich den Empfang dieser Abmahnung.", margin, y);
  doc.text("Die Unterschrift bedeutet keine Anerkennung des Inhalts.", margin, y + 5);
  y += 20;

  doc.line(margin, y, margin + 50, y);
  doc.line(pageWidth / 2 + 10, y, pageWidth - margin, y);
  y += 5;
  doc.text("Datum", margin, y);
  doc.text("Unterschrift Arbeitnehmer", pageWidth / 2 + 10, y);

  if (data.signatureUrl) {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = data.signatureUrl;
      await new Promise((resolve) => { img.onload = resolve; });
      doc.addImage(img, "PNG", pageWidth / 2 + 10, y - 25, 50, 20);
    } catch (e) {
      console.error("Could not add signature image:", e);
    }
  }

  // Add footer with company address and page numbers
  addDocumentFooter(doc, companyData);

  if (saveToFile) {
    doc.save(`Abmahnung_${data.personName.replace(/\s+/g, "_")}_${data.warningNumber}.pdf`);
  }
  return doc;
};

interface VehicleAssignmentData {
  personName: string;
  personAddress: string;
  position?: string;
  vehicleManufacturer: string;
  vehicleLicensePlate: string;
  documentDate?: string;
  effectiveDate: string;
  companyName: string;
  companyAddress: string;
  signatureUrl?: string;
}

export const exportVehicleAssignmentToPDF = async (data: VehicleAssignmentData, saveToFile: boolean = true): Promise<jsPDF> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let y = 20;

  // Load company logo
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  // Add company logo at top
  if (logoBase64) {
    await addCompanyLogo(doc, logoBase64, 15, 8, 60, 33);
    y = 48;
  }

  // Location and date on the right
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  const cityName = companyData.city || "Erftstadt";
  const documentDate = data.documentDate 
    ? format(new Date(data.documentDate), "dd.MM.yyyy", { locale: de })
    : format(new Date(), "dd.MM.yyyy", { locale: de });
  doc.text(`${cityName}, ${documentDate}`, pageWidth - margin, y, { align: "right" });
  y += 15;

  const addNewPageIfNeeded = (requiredSpace: number = 15) => {
    if (y > 262 - requiredSpace) {
      doc.addPage();
      y = 25;
    }
  };

  const addParagraph = (text: string, indent: number = 0) => {
    const splitLines = doc.splitTextToSize(text, pageWidth - 2 * margin - indent);
    for (const line of splitLines) {
      addNewPageIfNeeded(5);
      doc.text(line, margin + indent, y);
      y += 5;
    }
  };

  // Title
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "bold");
  doc.text("Vereinbarung über die Überlassung eines Dienstfahrzeugs", pageWidth / 2, y, { align: "center" });
  y += 15;

  // Parties
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text("Zwischen der Firma", margin, y);
  y += 10;

  doc.setFont("helvetica", "bold");
  doc.text(data.companyName, margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  const companyAddressLines = data.companyAddress.split(",").map(s => s.trim());
  for (const line of companyAddressLines) {
    doc.text(line, margin, y);
    y += 5;
  }
  doc.text("- nachfolgend \"Arbeitgeber\" genannt -", margin, y);
  y += 12;

  doc.text("und", margin, y);
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.text(data.personName, margin, y);
  y += 5;
  if (data.personAddress) {
    const addressParts = data.personAddress.split(",").map(s => s.trim());
    for (const part of addressParts) {
      doc.text(part, margin, y);
      y += 5;
    }
  }
  doc.text("- nachfolgend \"Arbeitnehmer\" genannt -", margin, y);
  y += 15;

  doc.setFont("helvetica", "bold");
  doc.text("wird folgende Vereinbarung getroffen:", margin, y);
  y += 15;

  // § 1 Überlassung eines Dienstfahrzeugs
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("§ 1 Überlassung eines Dienstfahrzeugs", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph(`(1) Der Arbeitgeber überlässt dem Arbeitnehmer ein Kraftfahrzeug der Marke ${data.vehicleManufacturer}, amtliches Kennzeichen ${data.vehicleLicensePlate || "__________"}, zur dienstlichen Nutzung.`);
  y += 4;
  addParagraph("(2) Der Arbeitgeber ist berechtigt, dem Arbeitnehmer jederzeit ein gleichwertiges oder anderes Dienstfahrzeug zur Verfügung zu stellen. In diesem Fall gilt diese Vereinbarung entsprechend fort.");
  y += 10;

  // § 2 Nutzung des Dienstfahrzeugs
  addNewPageIfNeeded(50);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("§ 2 Nutzung des Dienstfahrzeugs / keine private Nutzung", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("(1) Das Dienstfahrzeug darf ausschließlich vom Arbeitnehmer selbst geführt werden.");
  y += 4;
  addParagraph("(2) Eine private Nutzung des Dienstfahrzeugs ist ausdrücklich nicht gestattet. Ein geldwerter Vorteil entsteht nicht.");
  y += 4;
  addParagraph("(3) Das Mitnehmen des Dienstfahrzeugs zur Wohnung des Arbeitnehmers ist zulässig, soweit dies im Rahmen angeordneter Bereitschaftsdienste, oder zur Durchführung dienstlicher Fahrten erforderlich ist.");
  y += 4;
  addParagraph("(4) Erlaubt sind in diesem Zusammenhang: Fahrten zwischen Wohnung und Einsatzort, Fahrten zwischen Wohnung und Betrieb, notwendige Umwege, insbesondere zum Tanken, zur Fahrzeugpflege, zur Werkstatt oder zur Besorgung dienstlich erforderlicher Materialien.");
  y += 4;
  addParagraph("(5) Sonstige Privatfahrten, insbesondere Fahrten zu privaten Zwecken ohne dienstlichen Zusammenhang, sind untersagt.");
  y += 10;

  // § 3 Kosten
  addNewPageIfNeeded(30);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("§ 3 Kosten des Fahrzeugs", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Der Arbeitgeber trägt die Kosten für Betrieb, Wartung, Reparaturen, Versicherungen, Kfz-Steuer sowie die Kosten des Kraftstoffs im Rahmen der dienstlichen Nutzung.");
  y += 10;

  // § 4 Pflichten
  addNewPageIfNeeded(50);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("§ 4 Pflichten des Arbeitnehmers", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("(1) Der Arbeitnehmer ist verpflichtet,");
  addParagraph("a) die Fahrzeugpapiere (Zulassungsbescheinigung Teil I) im Fahrzeug mitzuführen und sorgfältig zu verwahren;", 5);
  addParagraph("b) den Arbeitgeber unverzüglich über fällige Wartungen, technische Mängel oder Beschädigungen zu informieren und Werkstatttermine nach Weisung des Arbeitgebers wahrzunehmen;", 5);
  addParagraph("c) das Fahrzeug pfleglich, sachgemäß und verkehrssicher zu behandeln;", 5);
  addParagraph("d) das absolute Rauchverbot im Dienstfahrzeug einzuhalten.", 5);
  y += 4;
  addParagraph("(2) Der Arbeitnehmer verpflichtet sich, die geltenden Verkehrsvorschriften einzuhalten. Die Nutzung des Fahrzeugs unter Alkohol-, Drogen- oder Medikamenteneinfluss, der die Fahrtüchtigkeit beeinträchtigt, ist untersagt.");
  y += 10;

  // § 5 Unfälle
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("§ 5 Unfälle, Verluste, Beschädigungen", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("(1) Unfälle, Verluste oder Beschädigungen des Fahrzeugs sind dem Arbeitgeber unverzüglich mitzuteilen. Reparaturen dürfen nur nach vorheriger Zustimmung des Arbeitgebers veranlasst werden.");
  y += 4;
  addParagraph("(2) Bei Unfällen mit Personenschäden, erheblichem Sachschaden oder unklarer Schuldfrage ist unverzüglich die Polizei hinzuzuziehen. Der Arbeitnehmer hat dem Arbeitgeber zeitnah einen schriftlichen Unfallbericht vorzulegen.");
  y += 10;

  // § 6 Haftung
  addNewPageIfNeeded(60);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("§ 6 Haftung des Arbeitnehmers", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("(1) Der Arbeitnehmer haftet für Schäden am Dienstfahrzeug nach den Grundsätzen der abgestuften Arbeitnehmerhaftung: bei Vorsatz oder grober Fahrlässigkeit voll, bei mittlerer Fahrlässigkeit anteilig, bei leichter Fahrlässigkeit nicht.");
  y += 4;
  addParagraph("(2) Bei Verstößen gegen die Pflichten aus § 4 dieser Vereinbarung haftet der Arbeitnehmer ebenfalls ausschließlich nach den Grundsätzen der abgestuften Arbeitnehmerhaftung.");
  y += 4;
  addParagraph("(3) Soweit eine Vollkaskoversicherung besteht und eintrittspflichtig ist, haftet der Arbeitnehmer bei grob fahrlässigem oder vorsätzlichem Verhalten bis zur Höhe der vereinbarten Selbstbeteiligung. Ein Verlust des Schadenfreiheitsrabatts ist nur in diesen Fällen zu ersetzen.");
  y += 4;
  addParagraph("(4) Der Arbeitnehmer stellt den Arbeitgeber von Haftpflichtansprüchen Dritter frei, soweit diese nicht durch die bestehende Kraftfahrzeug-Haftpflichtversicherung gedeckt sind und der Schaden vom Arbeitnehmer vorsätzlich oder grob fahrlässig verursacht wurde.");
  y += 4;
  addParagraph("(5) Im Übrigen verbleibt es bei den von der Rechtsprechung entwickelten Grundsätzen zur privilegierten Arbeitnehmerhaftung.");
  y += 10;

  // § 7 Rückgabe
  addNewPageIfNeeded(50);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("§ 7 Rückgabe des Fahrzeugs / kein Zurückbehaltungsrecht", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("(1) Der Arbeitgeber kann aus sachlichem Grund die Rückgabe des Dienstfahrzeugs verlangen. Sachliche Gründe sind insbesondere: eine länger andauernde Arbeitsunfähigkeit über den gesetzlichen Entgeltfortzahlungszeitraum hinaus, Kündigung des Arbeitsverhältnisses, Freistellung, Zeiten eines Beschäftigungsverbots oder der Elternzeit.");
  y += 4;
  addParagraph("(2) In diesen Fällen ist das Fahrzeug spätestens am folgenden Arbeitstag am Sitz des Arbeitgebers mit sämtlichen Schlüsseln und Unterlagen zurückzugeben.");
  y += 4;
  addParagraph("(3) Ein Zurückbehaltungsrecht besteht nicht, soweit es nicht auf unbestrittenen oder rechtskräftig festgestellten Forderungen beruht.");
  y += 10;

  // § 8 Entzug der Fahrerlaubnis
  addNewPageIfNeeded(30);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("§ 8 Entzug der Fahrerlaubnis / Fahrverbot", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Der Arbeitnehmer ist verpflichtet, den Arbeitgeber unverzüglich über den Entzug der Fahrerlaubnis oder ein Fahrverbot zu informieren.");
  y += 10;

  // § 9 Nutzung durch Dritte
  addNewPageIfNeeded(30);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("§ 9 Nutzung durch Dritte / Mitnahme Dritter", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Die Überlassung des Dienstfahrzeugs an Dritte ist untersagt. Die Mitnahme betriebsfremder Personen ist nicht gestattet.");
  y += 10;

  // § 10 Schlussbestimmungen
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("§ 10 Schlussbestimmungen", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("(1) Änderungen und Ergänzungen dieser Vereinbarung bedürfen der Schriftform. Individuell ausgehandelte Vereinbarungen bleiben hiervon unberührt.");
  y += 4;
  addParagraph("(2) Sollte eine Bestimmung dieser Vereinbarung unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Regelungen unberührt. Anstelle der unwirksamen Regelung tritt eine Regelung, die dem wirtschaftlichen Zweck am nächsten kommt.");
  y += 15;

  // Signature section
  addNewPageIfNeeded(50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`${cityName}, den ${formatDate(data.effectiveDate)}`, margin, y);
  y += 20;

  // Signature lines
  doc.setDrawColor(...COLORS.border);
  doc.line(margin, y, margin + 70, y);
  doc.line(pageWidth / 2 + 10, y, pageWidth - margin, y);
  
  y += 5;
  doc.setFontSize(9);
  doc.text("Arbeitgeber", margin, y);
  doc.text("Arbeitnehmer", pageWidth / 2 + 10, y);

  // Add signature image if available
  if (data.signatureUrl) {
    try {
      const img = new Image();
      img.src = data.signatureUrl;
      await new Promise((resolve) => { img.onload = resolve; });
      doc.addImage(img, "PNG", pageWidth / 2 + 10, y - 25, 50, 20);
    } catch (e) {
      console.error("Could not add signature image:", e);
    }
  }

  // Add footer with company address and page numbers
  addDocumentFooter(doc, companyData);

  if (saveToFile) {
    doc.save(`Fahrzeugueberlassung_${data.personName.replace(/\s+/g, "_")}.pdf`);
  }
  return doc;
};

interface SafetyInstructionData {
  personName: string;
  position?: string;
  department?: string;
  documentDate?: string;
  supervisorName?: string;
  companyName: string;
  signatureUrl?: string;
}

export const exportSafetyInstructionToPDF = async (data: SafetyInstructionData, saveToFile: boolean = true): Promise<jsPDF> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let y = 20;

  // Load company logo
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  // Add company logo at top
  if (logoBase64) {
    await addCompanyLogo(doc, logoBase64, 15, 8, 60, 33);
    y = 48;
  }

  // Location and date on the right
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  const cityName = companyData.city || "Erftstadt";
  const documentDate = data.documentDate 
    ? format(new Date(data.documentDate), "dd.MM.yyyy", { locale: de })
    : format(new Date(), "dd.MM.yyyy", { locale: de });
  doc.text(`${cityName}, ${documentDate}`, pageWidth - margin, y, { align: "right" });
  y += 10;

  const addNewPageIfNeeded = (requiredSpace: number = 15) => {
    if (y > 262 - requiredSpace) {
      doc.addPage();
      y = 25;
    }
  };

  const addParagraph = (text: string, indent: number = 0) => {
    const splitLines = doc.splitTextToSize(text, pageWidth - 2 * margin - indent);
    for (const line of splitLines) {
      addNewPageIfNeeded(5);
      doc.text(line, margin + indent, y);
      y += 5;
    }
  };

  const addBulletPoint = (text: string, indent: number = 5) => {
    addNewPageIfNeeded(10);
    doc.text("•", margin + indent, y);
    const splitLines = doc.splitTextToSize(text, pageWidth - 2 * margin - indent - 5);
    for (let i = 0; i < splitLines.length; i++) {
      if (i > 0) addNewPageIfNeeded(5);
      doc.text(splitLines[i], margin + indent + 5, y);
      y += 5;
    }
  };

  // Title
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.primary);
  doc.setFont("helvetica", "bold");
  doc.text("Sicherheitsunterweisung", pageWidth / 2, y, { align: "center" });
  y += 8;
  
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  doc.text("nach Arbeitsschutzgesetz (ArbSchG) und DGUV-Vorschriften", pageWidth / 2, y, { align: "center" });
  y += 5;
  doc.text("für Arbeiten auf Baustellen und in technischen Anlagen", pageWidth / 2, y, { align: "center" });
  y += 5;
  doc.text("(Elektro-, Bau-, Glasfaser- und Kupfernetzwerktechnik)", pageWidth / 2, y, { align: "center" });
  y += 12;

  // Header info
  doc.setFont("helvetica", "bold");
  doc.text(`Unternehmen: ${data.companyName}`, margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.text("Geltungsbereich: Alle Mitarbeiter, Auszubildende, Helfer", margin, y);
  y += 5;
  doc.text(`Datum: ${documentDate}`, margin, y);
  y += 5;
  doc.text(`Unterweisender (verantwortliche Führungskraft): ${data.supervisorName || "________________________"}`, margin, y);
  y += 5;
  doc.text(`Mitarbeiter: ${data.personName}${data.position ? ` (${data.position})` : ""}`, margin, y);
  y += 12;

  // Section 1
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("1. Rechtliche Grundlage und Ziel der Unterweisung", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Diese Sicherheitsunterweisung erfolgt auf Grundlage:");
  y += 2;
  addBulletPoint("§ 12 Arbeitsschutzgesetz (ArbSchG)");
  addBulletPoint('DGUV Vorschrift 1 "Grundsätze der Prävention"');
  addBulletPoint("einschlägiger DGUV-Regeln und Informationen");
  y += 3;
  addParagraph("Der Arbeitgeber ist verpflichtet, die Mitarbeiter über Gefahren am Arbeitsplatz sowie über Maßnahmen zur Vermeidung von Unfällen und Gesundheitsgefahren zu unterweisen.");
  y += 3;
  addParagraph("Ziel dieser Unterweisung ist es, dass jeder Mitarbeiter:");
  addBulletPoint("Gefahren erkennt,");
  addBulletPoint("Risiken richtig einschätzt,");
  addBulletPoint("sich sicherheitsgerecht verhält,");
  addBulletPoint("und aktiv zur Unfallvermeidung beiträgt.");
  y += 8;

  // Section 2
  addNewPageIfNeeded(50);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("2. Verantwortung und Mitwirkungspflicht", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Arbeitssicherheit ist keine freiwillige Leistung, sondern Pflicht für alle Beteiligten.");
  y += 3;
  doc.setFont("helvetica", "bold");
  doc.text("Pflichten des Arbeitgebers:", margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  addBulletPoint("Bereitstellung sicherer Arbeitsmittel");
  addBulletPoint("Organisation von Unterweisungen");
  addBulletPoint("Bereitstellung notwendiger PSA");
  y += 3;
  doc.setFont("helvetica", "bold");
  doc.text("Pflichten der Mitarbeiter:", margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  addBulletPoint("Einhaltung aller Sicherheitsregeln");
  addBulletPoint("bestimmungsgemäße Nutzung von Werkzeugen und PSA");
  addBulletPoint("Meldung von Gefahren, Mängeln und Unfällen");
  addBulletPoint("Unterlassung riskanter oder unsicherer Handlungen");
  y += 3;
  doc.setFont("helvetica", "italic");
  addParagraph("Wer unsicher ist, arbeitet nicht weiter, sondern fragt nach.");
  doc.setFont("helvetica", "normal");
  y += 8;

  // Section 3
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("3. Allgemeines Verhalten auf Baustellen", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Baustellen sind dynamische Arbeitsbereiche mit ständig wechselnden Gefahren.");
  y += 3;
  doc.setFont("helvetica", "bold");
  doc.text("Grundregeln:", margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  addBulletPoint("Baustellenregeln und Hausordnungen des Auftraggebers beachten");
  addBulletPoint("Anweisungen von Bauleitern, SiGeKo oder Vorgesetzten befolgen");
  addBulletPoint("Verkehrswege, Flucht- und Rettungswege freihalten");
  addBulletPoint("Keine Ablenkung durch private Nutzung von Mobiltelefonen während der Arbeit");
  y += 3;
  doc.setFont("helvetica", "italic");
  addParagraph("Gefährliche Situationen sind unverzüglich zu melden. Arbeiten dürfen nicht fortgesetzt werden, wenn eine akute Gefahr besteht.");
  doc.setFont("helvetica", "normal");
  y += 8;

  // Section 4
  addNewPageIfNeeded(50);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("4. Persönliche Schutzausrüstung (PSA)", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Die PSA dient dem Schutz vor unvermeidbaren Restrisiken. Je nach Tätigkeit sind erforderlich:");
  y += 3;
  addBulletPoint("Sicherheitsschuhe S3: Schutz vor Durchtritt, Quetschung, Rutschen");
  addBulletPoint("Schutzhelm: Schutz vor herabfallenden oder pendelnden Gegenständen");
  addBulletPoint("Warnkleidung: Erhöhung der Sichtbarkeit im Baustellen- und Verkehrsbereich");
  addBulletPoint("Schutzbrille: Schutz der Augen bei Bohr-, Trenn- und Glasfaserarbeiten");
  addBulletPoint("Schutzhandschuhe: Schutz vor Schnitt-, Stich- und Hautverletzungen");
  y += 3;
  addParagraph("PSA ist vor Arbeitsbeginn zu prüfen, sauber und funktionsfähig zu halten, bei Beschädigung sofort zu ersetzen.");
  y += 3;
  doc.setFont("helvetica", "bold");
  addParagraph("Arbeiten ohne vorgeschriebene PSA sind nicht zulässig.");
  doc.setFont("helvetica", "normal");
  y += 8;

  // Section 5
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("5. Stolper-, Rutsch- und Sturzgefahren", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Ein Großteil der meldepflichtigen Arbeitsunfälle entsteht durch Stolpern, Rutschen oder Stürzen. Daher gilt:");
  y += 3;
  addBulletPoint("Kabel, Schläuche und Leitungen so verlegen, dass keine Stolperstellen entstehen");
  addBulletPoint("Werkzeuge und Material nicht in Laufwegen ablegen");
  addBulletPoint("Unebenheiten, Öffnungen und Kanten sichern oder kennzeichnen");
  addBulletPoint("Bei Nässe, Staub oder Glätte erhöhte Vorsicht walten lassen");
  y += 3;
  doc.setFont("helvetica", "italic");
  addParagraph("Arbeitsplätze sind regelmäßig aufzuräumen. Ordnung ist Teil der Arbeitssicherheit.");
  doc.setFont("helvetica", "normal");
  y += 8;

  // Section 6
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("6. Arbeiten mit Leitern und in der Höhe", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Leitern dürfen nur verwendet werden, wenn sie für den Zweck geeignet sind. Dabei ist zu beachten:");
  y += 3;
  addBulletPoint("Leiter vor Benutzung auf Schäden prüfen");
  addBulletPoint("Leiter standsicher aufstellen");
  addBulletPoint("Leiter nicht übersteigen oder seitlich belasten");
  addBulletPoint("Keine schweren oder kraftaufwendigen Arbeiten von der Leiter aus");
  y += 3;
  addParagraph("Gerüste und Arbeitsbühnen dürfen nur benutzt werden, wenn sie ordnungsgemäß aufgebaut und freigegeben sind.");
  y += 8;

  // Section 7
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("7. Elektrische Gefährdungen", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Auch bei Netzwerk- und Glasfaserarbeiten besteht häufig Kontakt zu elektrischen Anlagen. Grundregeln:");
  y += 3;
  addBulletPoint("Arbeiten an elektrischen Anlagen nur durchführen, wenn diese freigeschaltet oder gesichert sind");
  addBulletPoint("Spannungsfreiheit prüfen");
  addBulletPoint("Beschädigte Kabel, Steckdosen oder Geräte nicht verwenden");
  addBulletPoint("Besondere Vorsicht bei Feuchtigkeit oder beengten Räumen");
  y += 3;
  doc.setFont("helvetica", "italic");
  addParagraph("Elektrische Gefahren dürfen nicht unterschätzt werden. Im Zweifel Arbeit einstellen und Rücksprache halten.");
  doc.setFont("helvetica", "normal");
  y += 8;

  // Section 8
  addNewPageIfNeeded(50);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("8. Besondere Gefahren bei Glasfaserarbeiten", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Glasfaserarbeiten bergen spezifische Gesundheitsrisiken. Zu beachten:");
  y += 3;
  addBulletPoint("Glasfasersplitter können Haut und Augen verletzen");
  addBulletPoint("Glasfaserreste niemals mit bloßen Händen aufnehmen");
  addBulletPoint("Schutzbrille tragen, insbesondere beim Trennen und Spleißen");
  addBulletPoint("Glasfaserabfälle ausschließlich in dafür vorgesehenen Behältern entsorgen");
  addBulletPoint("Niemals in aktive Glasfasern schauen (Laserstrahlung)");
  y += 3;
  doc.setFont("helvetica", "italic");
  addParagraph("Sauberkeit und Ordnung sind bei Glasfaserarbeiten zwingend erforderlich.");
  doc.setFont("helvetica", "normal");
  y += 8;

  // Section 9
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("9. Werkzeuge, Maschinen und Arbeitsmittel", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Arbeitsmittel dürfen nur bestimmungsgemäß eingesetzt werden. Vor Benutzung:");
  y += 3;
  addBulletPoint("Sichtprüfung auf Schäden");
  addBulletPoint("Funktionsprüfung");
  addBulletPoint("Sicherstellen, dass Schutzvorrichtungen vorhanden und funktionsfähig sind");
  y += 3;
  doc.setFont("helvetica", "bold");
  addParagraph("Defekte oder unsichere Geräte sind sofort außer Betrieb zu nehmen und zu melden. Improvisationen und Manipulationen sind verboten.");
  doc.setFont("helvetica", "normal");
  y += 8;

  // Section 10
  addNewPageIfNeeded(40);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("10. Ordnung, Sauberkeit und Brandschutz", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Unordnung erhöht das Unfall- und Brandrisiko. Deshalb:");
  y += 3;
  addBulletPoint("Arbeitsbereiche sauber halten");
  addBulletPoint("Abfälle regelmäßig entsorgen");
  addBulletPoint("Brandlasten vermeiden");
  addBulletPoint("Feuerlöscher und Fluchtwege freihalten");
  y += 3;
  addParagraph("Offenes Feuer und Rauchen sind nur in ausdrücklich erlaubten Bereichen gestattet.");
  y += 8;

  // Section 11
  addNewPageIfNeeded(50);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("11. Verhalten bei Unfällen, Notfällen und Beinahe-Unfällen", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Bei Unfällen:");
  y += 3;
  addBulletPoint("Ruhe bewahren");
  addBulletPoint("Erste-Hilfe leisten");
  addBulletPoint("Unfallstelle absichern");
  addBulletPoint("Vorgesetzten informieren");
  addBulletPoint("Bei schweren Unfällen Notruf absetzen");
  y += 3;
  doc.setFont("helvetica", "italic");
  addParagraph("Auch Beinahe-Unfälle sind zu melden. Sie helfen, zukünftige Unfälle zu vermeiden.");
  doc.setFont("helvetica", "normal");
  y += 8;

  // Section 12
  addNewPageIfNeeded(30);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("12. Arbeitsmedizinische Vorsorge", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Mitarbeiter haben an vorgeschriebenen arbeitsmedizinischen Vorsorgeuntersuchungen teilzunehmen, sofern diese angeboten oder erforderlich sind.");
  y += 3;
  addParagraph("Gesundheitliche Einschränkungen, die die Arbeitssicherheit betreffen, sind dem Arbeitgeber mitzuteilen.");
  y += 8;

  // Section 13
  addNewPageIfNeeded(30);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("13. Konsequenzen bei Verstößen", margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Die Einhaltung der Sicherheitsregeln ist verpflichtend. Verstöße können arbeitsrechtliche Maßnahmen nach sich ziehen.");
  y += 3;
  doc.setFont("helvetica", "bold");
  addParagraph("Sicherheit hat Vorrang vor Zeitdruck oder wirtschaftlichen Interessen.");
  doc.setFont("helvetica", "normal");
  y += 10;

  // Section 14 - Confirmation
  addNewPageIfNeeded(60);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("14. Bestätigung der Unterweisung", margin, y);
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addParagraph("Ich bestätige, dass ich diese Sicherheitsunterweisung erhalten habe, den Inhalt verstanden habe und die Sicherheitsregeln einhalte.");
  y += 15;

  // Signature lines
  doc.setDrawColor(...COLORS.border);
  doc.line(margin, y, margin + 50, y);
  doc.line(pageWidth / 2 + 10, y, pageWidth - margin, y);
  
  y += 5;
  doc.setFontSize(9);
  doc.text("Datum", margin, y);
  doc.text("Unterschrift Mitarbeiter", pageWidth / 2 + 10, y);

  // Add signature image if available
  if (data.signatureUrl) {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = data.signatureUrl;
      await new Promise((resolve) => { img.onload = resolve; });
      doc.addImage(img, "PNG", pageWidth / 2 + 10, y - 25, 50, 20);
    } catch (e) {
      console.error("Could not add signature image:", e);
    }
  }

  // Add footer with company address and page numbers
  addDocumentFooter(doc, companyData);

  if (saveToFile) {
    doc.save(`Sicherheitsunterweisung_${data.personName.replace(/\s+/g, "_")}.pdf`);
  }
  return doc;
};

// ============================================================
// Key Handover Protocol (Schlüssel-/Transponder-Übergabe)
// Designed to fit on one DIN A4 page using the company CI.
// ============================================================
export interface KeyHandoverData {
  personName: string;
  employeeNumber?: string;
  personAddressStreet?: string;
  personAddressZip?: string;
  personAddressCity?: string;
  closingTimes?: string;
  alarmPin?: string;
  keyType: string;
  keyNumber: string;
  keyDescription?: string;
  notes?: string;
  documentDate?: string;
  companyName: string;
  companyCity?: string;
  signatureUrl?: string;
}

export const exportKeyHandoverToPDF = async (
  data: KeyHandoverData,
  saveToFile: boolean = true
): Promise<jsPDF> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let y = 18;

  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  if (logoBase64) {
    await addCompanyLogo(doc, logoBase64, 15, 8, 55, 30);
  }

  doc.setFontSize(10);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  const cityName = data.companyCity || companyData.city || "Erftstadt";
  const documentDateStr = data.documentDate
    ? format(new Date(data.documentDate), "dd.MM.yyyy", { locale: de })
    : format(new Date(), "dd.MM.yyyy", { locale: de });
  doc.text(`${cityName}, ${documentDateStr}`, pageWidth - margin, y + 10, { align: "right" });

  y = 46;

  doc.setFillColor(...COLORS.primary);
  doc.rect(margin, y, pageWidth - 2 * margin, 9, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`${data.keyType.toUpperCase()}-ÜBERGABE`, pageWidth / 2, y + 6.2, { align: "center" });
  y += 14;

  doc.setTextColor(...COLORS.text);
  doc.setFontSize(10);
  const labelX = margin;
  const valueX = margin + 36;
  const lineH = 5.5;

  const addRow = (label: string, value: string) => {
    doc.setFont("helvetica", "normal");
    doc.text(label, labelX, y);
    doc.setFont("helvetica", "bold");
    doc.text(value || "—", valueX, y);
    y += lineH;
  };

  addRow("Personalnr.:", data.employeeNumber || "—");
  addRow("Name:", data.personName);
  const addrLine1 = data.personAddressStreet || "";
  const addrLine2 = [data.personAddressZip, data.personAddressCity].filter(Boolean).join(" ");
  doc.setFont("helvetica", "normal");
  doc.text("Anschrift:", labelX, y);
  doc.setFont("helvetica", "bold");
  if (addrLine1) {
    doc.text(addrLine1, valueX, y);
    y += lineH;
    if (addrLine2) {
      doc.text(addrLine2, valueX, y);
      y += lineH;
    }
  } else {
    doc.text(addrLine2 || "—", valueX, y);
    y += lineH;
  }

  y += 3;
  addRow("Schließzeiten:", data.closingTimes || "—");
  addRow("Alarmanlage-PIN:", data.alarmPin || "—");

  y += 4;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.primary);
  doc.text(`${data.keyType}-Übergabe:`, margin, y);
  y += 6;

  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const introText = `Hiermit bestätigt der o.g. Empfänger den Erhalt des folgenden Zugangsträgers (${data.keyType}):`;
  const introLines = doc.splitTextToSize(introText, pageWidth - 2 * margin);
  for (const l of introLines) {
    doc.text(l, margin, y);
    y += lineH;
  }
  y += 1;

  addRow(`${data.keyType}:`, data.keyNumber);
  if (data.keyDescription) addRow("Bezeichnung:", data.keyDescription);
  if (data.notes) {
    doc.setFont("helvetica", "normal");
    doc.text("Hinweise:", labelX, y);
    const noteLines = doc.splitTextToSize(data.notes, pageWidth - 2 * margin - 36);
    let ny = y;
    for (const nl of noteLines) {
      doc.setFont("helvetica", "normal");
      doc.text(nl, valueX, ny);
      ny += lineH;
    }
    y = ny;
  }

  y += 4;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.primary);
  doc.text("Zur Beachtung:", margin, y);
  y += 5;

  doc.setTextColor(...COLORS.text);
  doc.setFontSize(8.5);
  const sections: Array<{ title: string; items: string[] }> = [
    {
      title: "1. Schlüsselverwahrung",
      items: [
        "1.1 Der/die Empfänger/in von Schlüsseln ist für eine sichere Aufbewahrung verantwortlich. Er/Sie übernimmt die Haftung für den Gebrauch der erhaltenen Schlüssel und trägt die Folgen, die sich aus einem Verlust ergeben. Schlüssel dürfen nicht mit in den Urlaub genommen werden.",
        "1.2 Jegliche Weitergabe von Schlüsseln ist insbesondere im Interesse der/des empfangsberechtigten Schlüsselinhabers/in untersagt.",
        "1.3 Bei Verlust des Schlüssels ist dieser unverzüglich zu melden, damit dieser in der Anlage gesperrt wird.",
        "1.4 Bei Verlust wird die Neubeschaffung und Programmierung mit 25 Euro berechnet.",
      ],
    },
    {
      title: "2. Rückgabe von Schlüsseln",
      items: [
        "2.1 Bei Ausscheiden aus dem Unternehmen oder Niederlegung der zugesprochenen Aufgaben sind alle erhaltenen Schlüssel an die ausgebende Stelle zurückzugeben. Die Rückgabe wird bestätigt. Auf Verlangen wird eine Quittung ausgestellt. Sollten Schlüssel nicht zurückgegeben werden, werden die entstandenen Kosten zur Wiederherstellung der Sicherheit dem/der Schlüsselinhaber/in in Rechnung gestellt.",
      ],
    },
    {
      title: "3. Schlüsselverwaltung",
      items: [
        "3.1 Bei Problemen oder Zeitänderungen ist der Verantwortliche der " +
          (data.companyName || "Firma") +
          " zu informieren. Dieser übernimmt die Verwaltung der Schlüssel sowie des Schlosses.",
      ],
    },
  ];

  for (const sec of sections) {
    doc.setFont("helvetica", "bold");
    doc.text(sec.title, margin, y);
    y += 4;
    doc.setFont("helvetica", "normal");
    for (const it of sec.items) {
      const lines = doc.splitTextToSize(it, pageWidth - 2 * margin - 6);
      for (let i = 0; i < lines.length; i++) {
        doc.text(lines[i], margin + 6, y);
        y += 3.6;
      }
      y += 0.5;
    }
    y += 1.5;
  }

  if (y > 240) y = 240;
  y += 6;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLORS.primary);
  doc.text("Empfangsbestätigung:", margin, y);
  y += 8;

  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "normal");
  doc.text(`${cityName}, den ${documentDateStr}`, margin, y);

  const sigLineY = y + 8;
  doc.setDrawColor(...COLORS.border);
  doc.line(pageWidth / 2 + 5, sigLineY, pageWidth - margin, sigLineY);
  doc.setFontSize(8);
  doc.text("Unterschrift des Empfängers", pageWidth / 2 + 5, sigLineY + 4);

  if (data.signatureUrl) {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = data.signatureUrl;
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      doc.addImage(img, "PNG", pageWidth / 2 + 5, sigLineY - 18, 50, 18);
    } catch (e) {
      console.error("Could not add signature image:", e);
    }
  }

  addDocumentFooter(doc, companyData);

  if (saveToFile) {
    doc.save(`Schluesseluebergabe_${data.personName.replace(/\s+/g, "_")}.pdf`);
  }
  return doc;
};
